import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {BehaviorSubject} from 'rxjs/index';

@Component({
  selector: 'cg-search',
  templateUrl: './cg-search.component.html',
  styleUrls: ['./cg-search.component.scss']
})
export class CGSearchComponent implements OnInit {

  /**
   * Direct element ref to the search bar input
   * Used to trigger events directly on element
   */
  @ViewChild('cgSearchInput') searchField: ElementRef;
  /**
   * Tracks expansion status of the search input
   * @type {BehaviorSubject<boolean>}
   * @private
   */
  private _searchExpandedBS: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**
   * Observable representation of the expansion status behavior subject
   * @type {Observable<boolean>}
   */
  searchExpanded$ = this._searchExpandedBS.asObservable();
  /**
   * Internal only mutable string model
   */
  searchStringRaw: string|null = null;
  /**
   * Configuration Option
   * Min wait time in ms before sending subsequent search value update while actively typing
   * @type {number}
   */
  private minWaitTime = 200;
  /**
   * Configuration Option
   * Max wait time while actively typing before next search update is executed
   * @type {number}
   */
  private maxWaitTime = 4000;
  /**
   * Private variable used as a guard flag to prevent increasing wait time on every single key stroke
   * @type {boolean}
   */
  private rateIncreaseBlock = false;
  /**
   * Configuration Option
   * Specifies the wait multiplier used to increase the execution wait time
   * @type {number}
   */
  private waitIntervalMultiplier = 2;
  /**
   * Tracks current wait time;
   * @type {number}
   */
  private currentWaitTime = this.minWaitTime;
  /**
   * Variable to track the last time a search value was emitted
   * This is used to calculate the delta between emissions
   */
  private lastBroadcastTime: any;
  /**
   * Timer that can either be active or null
   * This is what's used to implemented the exponential backoff
   */
  private searchExecutionTimer;
  /**
   * Timer that controls the resetting of the wait time after a short delay
   */
  private resetWaitTimer;
  /**
   * Wait interval for the reset timer to kick in.
   * If the user hasn't typed a character in this amount of ms, then the wait interval will reset to minimum
   * @type {number}
   */
  private resetWaitTimerDelay = 1000;
  /**
   * Tracks the last value emitted to prevent duplicate search calls back to back
   */
  private lastValueEmitted: string = null;
  /**
   * Flag to allow wait time reset on broadcast.
   * @type {boolean}
   */
  private resetWaitOnNextBroadcast = false;
  /**
   * Event handler for consuming components to bind to for search value updates.
   * @type {EventEmitter<string>}
   */
  @Output() searchValueChanged = new EventEmitter<string|void>();
  /**
   * Event that is fired when the search bar is expanded from a collapsed state
   * @type {EventEmitter<any>}
   */
  @Output() searchExpanded = new EventEmitter();
  /**
   * Event that is fired when the search bar is collapsed from an expanded state
   * @type {EventEmitter<any>}
   */
  @Output() searchCollapsed = new EventEmitter();
  /**
   * Configuration option to allow customization of the placeholder text
   * By default none is shown
   * Default: ''
   * @type {string}
   */
  @Input() placeholderText = '';
  /**
   * Input to allow parent controller to set this control as expanded by default
   * Default: False
   * @type {boolean}
   */
  @Input() expandedByDefault = false;
  /**
   * Configuration option to turn on logs
   * Default: False
   * @type {boolean}
   */
  @Input() loggingEnabled = false;
  /**
   * Configuration option to allow auto focusing input on clicking search icon
   * Default: True
   * @type {boolean}
   */
  @Input() autoFocusInput = true;

  constructor() { }

  ngOnInit() {
    if (this.expandedByDefault) {
      this._searchExpandedBS.next(true);
    }
  }

  /**
   * Handler for user clicking the search icon to expand the search bar
   */
  expandSearch() {
    this._searchExpandedBS.next(true);
    if (this.loggingEnabled) {
      console.log(`SearchBar Expanded`);
    }
    this.searchExpanded.emit();
    if (this.autoFocusInput) {
      if (this.searchField) {
        this.searchField.nativeElement.focus();
      } else {
        console.log(`No Search Field Available To Focus`);
      }
    } else {
      console.log('Auto Focus Input disabled');
    }
  }

  /**
   * Handler for user clicking the X icon to collapse the search bar
   */
  collapseSearch() {
    this._searchExpandedBS.next(false);
    if (this.loggingEnabled) {
      console.log(`SearchBar Collapsed`);
    }
    this.lastValueEmitted = null;
    this.searchCollapsed.emit();
    this.searchValueChanged.emit();
    this.searchStringRaw = null;
  }

  /**
   * On Key Up handler
   */
  onSearchTextKeyUp() {
    this.cycleResetWaitTimer();
    this.startTimerIfNeeded();
  }

  /**
   * Destroys and creates a timeout call to clear set a flag for resetting the wait timer
   * This is used to allow the timer to reset after the user stops typing, but not until the next value is emitted
   */
  cycleResetWaitTimer() {
    if (this.resetWaitTimer) {
      clearTimeout(this.resetWaitTimer);
    }
    this.resetWaitTimer = setTimeout(() => {
      if (this.loggingEnabled) {
        console.log(`Resetting Wait Time on Next Broadcast = TRUE`);
      }
      this.resetWaitOnNextBroadcast = true;
      this.broadcastSearchValue();
    }, this.resetWaitTimerDelay);
  }

  /**
   * Resets the search execution timer, and turns off rate increase blocking
   */
  clearTimer() {
    if (this.searchExecutionTimer) {
      clearTimeout(this.searchExecutionTimer);
      this.searchExecutionTimer = null;
      this.rateIncreaseBlock = false;
    }
  }

  /**
   * This function will handle creation of the execution timer, and the wait time increases.
   */
  startTimerIfNeeded() {
    if (!this.searchExecutionTimer) {
      if (this.loggingEnabled) {
        console.log(`Starting broadcast timer with wait timeout: `, this.currentWaitTime);
      }
      this.searchExecutionTimer = setTimeout(() => {
        this.broadcastSearchValue();
      }, this.currentWaitTime);
    } else {
      if (!this.rateIncreaseBlock && this.currentWaitTime < this.maxWaitTime) {
        this.rateIncreaseBlock = true;
        let newWaitTime = this.currentWaitTime * this.waitIntervalMultiplier;
        if (newWaitTime > this.maxWaitTime) {
          newWaitTime = this.maxWaitTime;
        }
        if (this.loggingEnabled) {
          console.log(`Updating Wait Time: \n
          Last Value: ${this.currentWaitTime} \n
          New  Value: ${newWaitTime}`);
        }
        this.currentWaitTime = newWaitTime;
      }
    }
  }

  /**
   * Emits the current search value and clears the timer
   * If logging is enabled, logs will show what value is bring broadcast and time since last broadcast
   */
  broadcastSearchValue() {
    const trimmedValue = this.searchStringRaw ? this.searchStringRaw.trim() : null;
    if (this.lastValueEmitted !== trimmedValue && trimmedValue !== '') {
      this.lastValueEmitted = trimmedValue;
      const timeSinceLastBroadcast = this.calcLastBroadcastTime();
      if (this.loggingEnabled) {
        console.log(`Broadcasting Current Search Value -> \n
        Broadcasting latest search value: ${this.lastValueEmitted} \n
        Time since last broadcast: ${timeSinceLastBroadcast}`);
      }
      this.searchValueChanged.emit(this.lastValueEmitted);
    }

    if (this.resetWaitOnNextBroadcast) {
      this.resetWaitOnNextBroadcast = false;
      this.initDefaultWaitTimer();
    }
    this.clearTimer();
  }

  /**
   * Assigns the min wait time to the current wait time and logs the value change
   */
  initDefaultWaitTimer() {
    this.currentWaitTime = this.minWaitTime;
    if (this.loggingEnabled) {
      console.log(`Resetting Wait Time: \n
        Last Value: ${this.currentWaitTime} \n
        New  Value: ${this.minWaitTime}`);
    }
  }

  /**
   * Calculates the last broadcast time and returns the time interval since last broadcast
   * @returns {any}
   */
  calcLastBroadcastTime() {
    const currentTime = Date.now();
    let timeSinceLastBroadcast;
    if (this.lastBroadcastTime) {
      timeSinceLastBroadcast = currentTime - this.lastBroadcastTime;
    }
    this.lastBroadcastTime = currentTime;
    return timeSinceLastBroadcast;
  }

}
